import React, { useMemo } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { App } from "./App";
import { buildTheme, isThemeId, resolveModule, type ThemeId } from "./theme";
import type { Theme } from "@mui/material/styles";
import { useResolvedColorMode } from "@/store/colorMode";
import { useDensity } from "@/store/density";
import { useEffectiveThemeId } from "@/store/theme";
import { PwaInstallBanner } from "@/components/PwaInstallBanner";
import { GlobalToast } from "@/components/GlobalToast";
import { notify } from "@/store/toast";

const queryClient = new QueryClient({
  // Opt-in toasts: any mutation that sets meta.successMessage gets a success snackbar.
  mutationCache: new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const msg = mutation.meta?.successMessage as string | undefined;
      if (msg) notify.success(msg);
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

// PWA service worker: register only in production builds. In dev, a service
// worker caches the app shell and serves a stale bundle (breaks live reload), so
// we proactively unregister any existing SW and clear its caches.
if ("serviceWorker" in navigator) {
  if (import.meta.env.PROD) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    });
  } else {
    navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
    if (window.caches) caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
  }
}

/**
 * Temporary review hatch: `?theme=teal` forces a palette for this page load only.
 *
 * Phase 3 replaces this with a persisted preference and a picker in the topbar. Until then it is
 * how the new palettes get reviewed on real screens. Read once at module load — the value is not
 * stored anywhere, so a plain reload returns to the default.
 */
const QUERY_THEME: ThemeId | null = (() => {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("theme");
  return isThemeId(raw) ? raw : null;
})();

/**
 * Built themes, keyed by every input that affects them.
 *
 * The departmental theme swaps its accent on navigation, so without this the app would rebuild a
 * theme on every route change. Seven modules x two modes x two densities is a small, bounded set
 * that is built once per session and then reused.
 */
const themeCache = new Map<string, Theme>();

/** Subscribes to the appearance stores and rebuilds the MUI theme when any of them changes. */
function ThemedApp() {
  // Resolved, not the raw preference: "system" has already been turned into light or dark here.
  const mode = useResolvedColorMode();
  const density = useDensity((s) => s.density);
  // Already resolved through the precedence chain: user choice, tenant default, built-in.
  const effective = useEffectiveThemeId();
  const themeId = QUERY_THEME ?? effective;

  // Only the departmental theme cares where you are; the rest ignore the module entirely.
  const { pathname } = useLocation();
  const moduleKey = themeId === "departmental" ? resolveModule(pathname) : null;

  const cacheKey = `${themeId}:${mode}:${density}:${moduleKey ?? "-"}`;
  const theme = useMemo(() => {
    let built = themeCache.get(cacheKey);
    if (!built) {
      built = buildTheme(themeId, mode, density, moduleKey);
      themeCache.set(cacheKey, built);
    }
    return built;
    // cacheKey encodes every input above; listing them again would only risk them drifting apart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
      <GlobalToast />
      <PwaInstallBanner />
    </ThemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemedApp />
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
